import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Shuffle, Copy, Settings, RotateCcw, Dices } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

const RandomNumberGenerator: React.FC = () => {
  const [minValue, setMinValue] = useState(1);
  const [maxValue, setMaxValue] = useState(100);
  const [quantity, setQuantity] = useState(1);
  const [allowDuplicates, setAllowDuplicates] = useState(true);
  const [generatedNumbers, setGeneratedNumbers] = useState<number[]>([]);
  const [history, setHistory] = useState<Array<{ numbers: number[]; range: string; time: string }>>([]);

  useSEO({
    title: 'Random Number Generator - Generate Random Numbers Online',
    description: 'Free online random number generator. Generate single or multiple random numbers within any range with options for duplicates.',
    keywords: 'random number generator, random numbers, number picker, lottery numbers, random tool',
    canonical: 'https://snap-tools.online/calc/random-number-generator'
  });

  const generateRandomNumbers = () => {
    if (minValue >= maxValue) {
      toast.error('Maximum value must be greater than minimum value');
      return;
    }

    if (!allowDuplicates && quantity > (maxValue - minValue + 1)) {
      toast.error('Cannot generate more unique numbers than available in range');
      return;
    }

    const numbers: number[] = [];
    const availableNumbers = new Set<number>();

    // Create set of available numbers for unique generation
    if (!allowDuplicates) {
      for (let i = minValue; i <= maxValue; i++) {
        availableNumbers.add(i);
      }
    }

    for (let i = 0; i < quantity; i++) {
      let randomNum: number;
      
      if (allowDuplicates) {
        randomNum = Math.floor(Math.random() * (maxValue - minValue + 1)) + minValue;
      } else {
        if (availableNumbers.size === 0) break;
        const availableArray = Array.from(availableNumbers);
        const randomIndex = Math.floor(Math.random() * availableArray.length);
        randomNum = availableArray[randomIndex];
        availableNumbers.delete(randomNum);
      }
      
      numbers.push(randomNum);
    }

    setGeneratedNumbers(numbers);

    // Add to history
    const historyEntry = {
      numbers,
      range: `${minValue}-${maxValue} (${quantity} ${quantity === 1 ? 'number' : 'numbers'})`,
      time: new Date().toLocaleTimeString()
    };
    setHistory(prev => [historyEntry, ...prev.slice(0, 9)]);

    toast.success(`Generated ${numbers.length} random ${numbers.length === 1 ? 'number' : 'numbers'}`);
  };

  const copyNumbers = () => {
    if (generatedNumbers.length === 0) {
      toast.error('No numbers to copy');
      return;
    }
    
    const numbersText = generatedNumbers.join(', ');
    navigator.clipboard.writeText(numbersText);
    toast.success('Numbers copied to clipboard');
  };

  const clearHistory = () => {
    setHistory([]);
    toast.success('History cleared');
  };

  const presetRanges = [
    { name: '1-6 (Dice)', min: 1, max: 6 },
    { name: '1-10', min: 1, max: 10 },
    { name: '1-100', min: 1, max: 100 },
    { name: '1-1000', min: 1, max: 1000 },
    { name: '0-255 (Byte)', min: 0, max: 255 },
    { name: '1-52 (Cards)', min: 1, max: 52 }
  ];

  const quickGenerate = (min: number, max: number, qty: number = 1) => {
    setMinValue(min);
    setMaxValue(max);
    setQuantity(qty);
    
    const numbers: number[] = [];
    for (let i = 0; i < qty; i++) {
      const randomNum = Math.floor(Math.random() * (max - min + 1)) + min;
      numbers.push(randomNum);
    }
    setGeneratedNumbers(numbers);

    const historyEntry = {
      numbers,
      range: `${min}-${max} (${qty} ${qty === 1 ? 'number' : 'numbers'})`,
      time: new Date().toLocaleTimeString()
    };
    setHistory(prev => [historyEntry, ...prev.slice(0, 9)]);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 sm:py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 sm:mb-12"
        >
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-xl sm:rounded-2xl mb-4 sm:mb-6 shadow-lg">
            <Shuffle className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#8438ff] mb-3 sm:mb-4">
            Random Number Generator
          </h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Generate random numbers within any range. Perfect for games, sampling, passwords, 
            lottery numbers, and statistical applications.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
          className="mb-6 sm:mb-8"
        />

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Generator */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-6"
            >
              <div className="flex items-center gap-2 mb-6">
                <Settings className="h-5 w-5 text-[#8438ff]" />
                <h2 className="text-xl font-semibold text-gray-900">Generator Settings</h2>
              </div>
              
              {/* Range Settings */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Minimum Value
                  </label>
                  <input
                    type="number"
                    value={minValue}
                    onChange={(e) => setMinValue(parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Maximum Value
                  </label>
                  <input
                    type="number"
                    value={maxValue}
                    onChange={(e) => setMaxValue(parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                  />
                </div>
              </div>

              {/* Quantity and Options */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quantity
                  </label>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    min="1"
                    max="1000"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={allowDuplicates}
                      onChange={(e) => setAllowDuplicates(e.target.checked)}
                      className="w-4 h-4 text-[#8438ff] border-gray-300 rounded focus:ring-[#8438ff]"
                    />
                    <span className="text-sm text-gray-700">Allow duplicates</span>
                  </label>
                </div>
              </div>

              {/* Generate Button */}
              <button
                onClick={generateRandomNumbers}
                className="w-full mb-6 px-6 py-3 bg-gradient-to-r from-[#8438ff] to-[#7228ee] text-white rounded-lg hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
              >
                <Shuffle className="h-5 w-5" />
                Generate Random Numbers
              </button>

              {/* Results */}
              {generatedNumbers.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">Generated Numbers</h3>
                    <button
                      onClick={copyNumbers}
                      className="flex items-center gap-1 px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                    >
                      <Copy className="h-3 w-3" />
                      Copy
                    </button>
                  </div>
                  <div className="p-4 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg">
                    <div className="flex flex-wrap gap-2">
                      {generatedNumbers.map((number, index) => (
                        <span
                          key={index}
                          className="px-3 py-2 bg-white border border-gray-200 rounded-lg font-mono text-lg font-semibold text-gray-800 shadow-sm"
                        >
                          {number}
                        </span>
                      ))}
                    </div>
                    <div className="mt-3 text-sm text-gray-600">
                      Range: {minValue} to {maxValue} • Generated: {generatedNumbers.length} {generatedNumbers.length === 1 ? 'number' : 'numbers'}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Preset Ranges */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Quick Presets</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {presetRanges.map((preset, index) => (
                    <button
                      key={index}
                      onClick={() => quickGenerate(preset.min, preset.max)}
                      className="p-3 text-sm bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors text-center"
                    >
                      <div className="font-medium text-gray-900">{preset.name}</div>
                      <div className="text-xs text-gray-500 mt-1">{preset.min} - {preset.max}</div>
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4"
            >
              <div className="flex items-center gap-2 mb-4">
                <Dices className="h-5 w-5 text-[#8438ff]" />
                <h3 className="font-semibold text-gray-900">Quick Actions</h3>
              </div>
              <div className="space-y-3">
                <button
                  onClick={() => quickGenerate(1, 6)}
                  className="w-full p-3 text-left bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors"
                >
                  <div className="font-medium text-blue-900">Roll a Die</div>
                  <div className="text-sm text-blue-600">Generate 1-6</div>
                </button>
                <button
                  onClick={() => quickGenerate(1, 6, 2)}
                  className="w-full p-3 text-left bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg transition-colors"
                >
                  <div className="font-medium text-green-900">Roll Two Dice</div>
                  <div className="text-sm text-green-600">Generate 2 numbers (1-6)</div>
                </button>
                <button
                  onClick={() => quickGenerate(1, 49, 6)}
                  className="w-full p-3 text-left bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg transition-colors"
                >
                  <div className="font-medium text-purple-900">Lottery Numbers</div>
                  <div className="text-sm text-purple-600">6 numbers (1-49)</div>
                </button>
                <button
                  onClick={() => quickGenerate(0, 1)}
                  className="w-full p-3 text-left bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors"
                >
                  <div className="font-medium text-gray-900">Coin Flip</div>
                  <div className="text-sm text-gray-600">0 or 1</div>
                </button>
              </div>
            </motion.div>

            {/* History */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Recent Generations</h3>
                {history.length > 0 && (
                  <button
                    onClick={clearHistory}
                    className="p-1 text-gray-500 hover:text-red-500 transition-colors"
                    title="Clear history"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {history.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-4">No generations yet</p>
                ) : (
                  history.map((entry, index) => (
                    <div
                      key={index}
                      className="p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex flex-wrap gap-1 mb-2">
                        {entry.numbers.map((num, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-white border border-gray-200 rounded text-sm font-mono"
                          >
                            {num}
                          </span>
                        ))}
                      </div>
                      <div className="text-xs text-gray-500">
                        {entry.range} • {entry.time}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
          className="mt-8 sm:mt-12"
        />
      </div>
    </div>
  );
};

export default RandomNumberGenerator;
